import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchCustomerById,
  updateCustomerProfile,
  getAllClients,
} from "../../../redux/customer/customerSlice";
import OrganizationDetails from "./OrganizationDetails";
import ContactDetails from "./ContactDetails";
import CustomerProjects from "./CustomerProjects";
import CustomerDynamicForms from "./CustomerDynamicForms";
import UserTable from "./UserTable";
import CustomerQuestionBank from "./CustomerQuestionBank";
import CardsBorder from "../../../components/Cards/CardsBorder";
import CustomerDetailForm from "./CustomerDetailForm";
import UserDetailPage from "../../../components/common/UserDetailPage/UserDetailPage";
import CustomerGroup from "./CustomerGroup";
import { ReactComponent as BackIcon } from "../../../assets/svgs/back-button.svg";
import CustomerSupportTickets from "./CustomerSupportTickets";
import RolesPage from "./Roles/Roles";
import TextStyles from "../../../components/Elements/TextStyles/TextStyles";
import CustomerDocument from "./CustomerDocument";
import CustomerLicense from "./CustomerLicense";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import CustomerSetupDetails from "./CustomerSetupDetails";
import { sanitizeBillingPayload } from "../../../utils/billingEmailUtils";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import { useParams } from "react-router-dom";
import Department from "./Department";
import Location from "./location";
import OrganizationSetup from "./OrganizationSetup";
import CustomerOrganizationSetup from "./CustomerOrganizationSetup";
const CustomerDetailPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentCustomer, saving } = useSelector((state) => state.customer);
  const userName = currentCustomer?.organization?.name || currentCustomer?.name || location.state?.userName || "";
  const customerId = currentCustomer?.userId || "";

  const { userId } = useParams();
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [billingPassportFile, setBillingPassportFile] = useState(null);
  const [eachUserId, setEachUserId] = useState("");
  const [openUserDetailsPage, setOpenUserDetailsPage] = useState(false);
  const [popup, setPopup] = useState({
    visible: false,
    type: "success",
    title: "",
    message: "",
  });

  const toggleMoreInfo = () => setShowMoreInfo(!showMoreInfo);

  // ─── Local form state (editable copy of Redux currentCustomer) ──────────
  const [basicInformation, setBasicInformation] = useState({
    name: "",
    username: "",
    email: "",
    location: "",
    email:"",
    description: "",
    contactNumber_1: "",
    emailId_1: "",
    contactNumber_2: "",
    emailId_2: "",
    primaryContactNumber: "",
    primaryEmailId: "",
  });

  const isInitialized = useRef(false);

  const [organizationDetails, setOrganizationDetails] = useState({
    name: "",
    natureOfBusiness: "",
    address: { city: "", state: "", pinCode: "", country: "" },
    cin: { number: "" },
    pan: { number: "" },
    vat: { number: "" },
    duns: { number: "" },
  });

  const [contactDetails, setContactDetails] = useState({
    organizationName: "",
    contactPersonName: "",
    email: "",
    countryCode: "",
    contactNumber: "",
    address: { city: "", state: "", pinCode: "", country: "" },
    billingContact: {
      contactPersonName: "",
      contactNumber: "",
      email: "",
    },
  });

  const [orgFiles, setOrgFiles] = useState({
    cin: null,
    pan: null,
    vat: null,
    duns: null,
  });

  const [identityFile, setIdentityFile] = useState(null);

  // ─── Fetch customer from API via Redux on mount ─────────────────────────
  useEffect(() => {
    if (!userId) {
      navigate("/customer");
      return;
    }
    dispatch(fetchCustomerById(userId));
  }, [dispatch, userId, navigate]);

  // ─── Populate form fields when Redux data arrives ───────────────────────
  useEffect(() => {
    if (currentCustomer && !isInitialized.current) {
      const c = currentCustomer;
      const primary =
        c.contacts?.find((ct) => ct.isPrimary) || c.contacts?.[0] || {};
      const secondary = c.contacts?.find((ct) => !ct.isPrimary) || {};
      setBasicInformation({
        name: c.name || "",
        organizationName:c.organization?.name||"",
        username: c.username || "",
        email: c.email || "",
        location: c.location || "",
        description: c.description || "",
        contactNumber_1: primary.contactNumber || "",
        emailId_1: primary.email || "",
        contactNumber_2: secondary.contactNumber || "",
        emailId_2: secondary.email || "",
        primaryContactNumber: primary.isPrimary
          ? primary.contactNumber || ""
          : "",
        primaryEmailId: primary.isPrimary ? primary.email || "" : "",
      });

      setOrganizationDetails({
        name: c.organization?.name || "",
        natureOfBusiness: c.organization?.natureOfBusiness || "",
        address: c.organization?.address || { city: "", state: "", pinCode: "", country: "" },
        cin: { number: c.organization?.cin?.number || "" },
        pan: { number: c.organization?.pan?.number || "" },
        vat: { number: c.organization?.vat?.number || "" },
        duns: { number: c.organization?.duns?.number || "" },
      });

      setContactDetails({
        organizationName: c.billing?.organizationName || "",
        contactPersonName: c.billing?.contactPersonName || "",
        email: c.billing?.email || "",
        countryCode: c.billing?.countryCode || "",
        contactNumber: c.billing?.contactNumber || "",
        address: c.billing?.address || { city: "", state: "", pinCode: "", country: "" },
        billingContact: {
          contactPersonName: c.billing?.billingContact?.contactPersonName || "",
          contactNumber: c.billing?.billingContact?.contactNumber || "",
          email:
            c.billing?.billingContact?.emailVerification?.pendingEmail ||
            c.billing?.billingContact?.email ||
            "",
        },
      });
      isInitialized.current = true;
    }
  }, [currentCustomer]);

  // Reset initialization flag if user ID changes to load a new customer
  useEffect(() => {
    isInitialized.current = false;
  }, [userId]);

  const handleOrgChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setOrganizationDetails(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else if (["cin", "pan", "vat", "duns"].includes(name)) {
      setOrganizationDetails(prev => ({
        ...prev,
        [name]: { ...prev[name], number: value }
      }));
    } else if (name.startsWith("billing_") || name.startsWith("billing.")) {
      // Redirect billing fields from OrganizationDetails to contactDetails
      const actualName = name.replace("billing_", "").replace("billing.", "");
      setContactDetails(prev => ({ ...prev, [actualName]: value }));
    } else {
      setOrganizationDetails(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleOrgFileSelect = (key, file) => {
    setOrgFiles(prev => ({ ...prev, [key]: file }));
  };
  const handleBillingPassportFileSelect = (file) => {
    setBillingPassportFile(file);
  };
  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setContactDetails(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else if (name.startsWith("billingContact.")) {
      const field = name.split(".")[1];
      setContactDetails(prev => ({
        ...prev,
        billingContact: { ...prev.billingContact, [field]: value }
      }));
    } else {
      setContactDetails(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleIdentityFileSelect = (file) => {
    setIdentityFile(file);
  };

  const handleChange = (e) => {
    setBasicInformation({
      ...basicInformation,
      [e.target.name]: e.target.value,
    });
  };

  const handlePrimarySelection = (option) => {
    if (option === "contact_1") {
      setBasicInformation({
        ...basicInformation,
        primaryContactNumber: basicInformation.contactNumber_1,
        primaryEmailId: basicInformation.emailId_1,
      });
    } else if (option === "contact_2") {
      setBasicInformation({
        ...basicInformation,
        primaryContactNumber: basicInformation.contactNumber_2,
        primaryEmailId: basicInformation.emailId_2,
      });
    }
  };

  const showPopupMsg = (type, title, message) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => setPopup((prev) => ({ ...prev, visible: false })), 3000);
  };

  // ─── Build payload & dispatch PUT on Save ───────────────────────────────
  const handleSave = async () => {
    if (!userId) return;

    // 1. Prepare individual JSON objects
    const basicInfoPayload = {
      name: basicInformation.name,
      username: basicInformation.username,
      email: basicInformation.email,
      location: basicInformation.location,
      description: basicInformation.description,
    };

    const contactsPayload = [
      {
        contactNumber: basicInformation.contactNumber_1,
        email: basicInformation.emailId_1,
        isPrimary: basicInformation.primaryContactNumber === basicInformation.contactNumber_1,
      },
    ];
    if (basicInformation.contactNumber_2 || basicInformation.emailId_2) {
      contactsPayload.push({
        contactNumber: basicInformation.contactNumber_2,
        email: basicInformation.emailId_2,
        isPrimary: basicInformation.primaryContactNumber === basicInformation.contactNumber_2,
      });
    }

    const organizationPayload = {
      name: organizationDetails.name,
      natureOfBusiness: organizationDetails.natureOfBusiness,
      address: organizationDetails.address,
      cin: { number: organizationDetails.cin.number },
      pan: { number: organizationDetails.pan.number },
      vat: { number: organizationDetails.vat.number },
      duns: { number: organizationDetails.duns.number },
    };

    const { billing: billingPayload, rejected: emailChangeRejected } =
      sanitizeBillingPayload({ ...contactDetails }, currentCustomer);

    // 2. Prepare FormData
    const formData = new FormData();

    // Append each section as a JSON string under its own field name
    formData.append("basicInfo", JSON.stringify(basicInfoPayload));
    formData.append("contacts", JSON.stringify(contactsPayload));
    formData.append("organization", JSON.stringify(organizationPayload));
    formData.append("billing", JSON.stringify(billingPayload));










    // 3. Append files separately as individual fields
    // if (orgFiles.cin) formData.append("cin", orgFiles.cin);

    // const panFile = identityFile || orgFiles.pan;
    // if (panFile) formData.append("pan", panFile);

    // if (orgFiles.vat) formData.append("vat", orgFiles.vat);
    // if (orgFiles.duns) formData.append("duns", orgFiles.duns);


    if (orgFiles.cin) formData.append("cinFile", orgFiles.cin);
    if (orgFiles.pan) formData.append("panFile", orgFiles.pan);
    if (orgFiles.vat) formData.append("vatFile", orgFiles.vat);
    if (orgFiles.duns) formData.append("dunsFile", orgFiles.duns);


    const passportFile = billingPassportFile || identityFile;
    if (passportFile) {
      formData.append("billingPassportFile", passportFile);
    }

    const resultAction = await dispatch(
      updateCustomerProfile({ id: userId, data: formData }),
    );
    if (updateCustomerProfile.fulfilled.match(resultAction)) {
      const apiRejected = resultAction.payload?.emailChangeRejected;
      const successMessage =
        emailChangeRejected || apiRejected
          ? "Profile saved. Billing contact email was not changed — verify OTP first."
          : "Customer profile updated successfully";
      showPopupMsg(
        emailChangeRejected || apiRejected ? "warning" : "success",
        emailChangeRejected || apiRejected ? "Email not updated" : "Success",
        successMessage,
      );
      isInitialized.current = false;
      dispatch(fetchCustomerById(userId));
      dispatch(getAllClients({ pageNo: 1, limit: 100 }));
      setIdentityFile(null);
    } else {
      showPopupMsg("error", "Error", resultAction.payload || "Failed to update profile");
    }
    
  };

  return (
    <div className="customer-detail-page w-full max-w-full">
      {!openUserDetailsPage ? (
        <div className="w-full max-w-full min-w-0">
          <div className="px-4 md:px-10 mt-4 w-full max-w-full min-w-0">
            <div
              className="flex justify-end text-xs"
              style={{ fontFamily: "Calibri" }}
            >
              <p>Home </p>
              <span> / </span>
              <p>customer </p> <span> / </span>
              <TextStyles color="gradient"> {userName}</TextStyles>
            </div>
            <div className="flex justify-between">
              <div className="flex my-auto">
                <BackIcon
                  onClick={() => {
                    const from = location.state?.from || "/customer";
                    navigate(from);
                  }}
                />
                <div>
                  <TextStyles color="gradient" style={{ fontSize: "20px" }}>
                    <p className="mt-3"> {userName} </p>
                  </TextStyles>
                </div>
              </div>
              <div>
                <CustomButton
                  buttonType="primary"
                  variant="ok"
                  buttonText="LOGIN AS A USER"
                  className="text-sm h-[50px]"
                />
              </div>
            </div>
            <div>
              <CardsBorder>
                <CustomerDetailForm
                  handleChange={handleChange}
                  userName={userName}
                  userId={currentCustomer?.userId || customerId}
                  basicInformation={basicInformation}
                  showMoreInfo={showMoreInfo}
                  toggleMoreInfo={toggleMoreInfo}
                  handlePrimarySelection={handlePrimarySelection}
                  OrganizationDetails={OrganizationDetails}
                  ContactDetails={ContactDetails}
                  handleBillingPassportFileSelect={handleBillingPassportFileSelect}
                  onSave={handleSave}
                  saving={saving}
                  organizationDetails={organizationDetails}
                  contactDetails={contactDetails}
                  handleOrgChange={handleOrgChange}
                  handleOrgFileSelect={handleOrgFileSelect}
                  handleBillingChange={handleBillingChange}
                  handleIdentityFileSelect={handleIdentityFileSelect}
                  onBillingEmailVerified={(verifiedEmail) => {
                    setContactDetails((prev) => ({
                      ...prev,
                      billingContact: {
                        ...prev.billingContact,
                        email: verifiedEmail,
                      },
                    }));
                  }}
                />
              </CardsBorder>
            </div>
          </div>

          <div>
            <CustomerDocument clientId={userId} />
          </div>
          <CustomerSetupDetails clientId={userId} />
          <CustomerLicense />

          <UserTable
            clientId={userId}
            setOpenUserDetailsPage={setOpenUserDetailsPage}
            setEachUserId={setEachUserId}
          />

          <RolesPage clientId={userId} />
          <CustomerProjects
            clientId={userId}
            basicInformation={basicInformation}
          />

          <CustomerGroup />
          <CustomerDynamicForms clientId={userId} />
          <CustomerQuestionBank clientId={userId} />
          <div className="mx-4 md:mx-10 mt-6 space-y-6 ">
            {/* <Location />
            <Department /> */}
            <CustomerOrganizationSetup/>
          </div>
          <CustomerSupportTickets clientId={userId} />
          {/* <OrganizationSetup clientId={userId} orgSetupType={orgSetup} /> */}
        </div>
      ) : (
        <UserDetailPage
          setOpenUserDetailsPage={setOpenUserDetailsPage}
          userId={eachUserId}
          clientId={userId}
        />
      )}

      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
            onClose={() => setPopup((prev) => ({ ...prev, visible: false }))}
          />
        </div>
      )}

      <style>{`
        .customer-detail-page,
        .customer-detail-page * {
          box-sizing: border-box;
        }

        .customer-detail-page > div,
        .customer-detail-page .ant-table-wrapper,
        .customer-detail-page .ant-table-container,
        .customer-detail-page .ant-table-content {
          max-width: 100%;
          min-width: 0;
        }

        .customer-detail-page .ant-table-wrapper,
        .customer-detail-page .ant-table-content {
          overflow-x: auto;
        }
      `}</style>
    </div>
  );
};

export default CustomerDetailPage;
